import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { CRASH_ID_KEY } from '../utils'
import {
  CRASH_SUMMARY_FIELD_KEYS,
  getMatchColumnsForFields,
  getMatchDetailSections,
  getPersonDetailFieldKeys,
  groupPeopleByUnit,
  UNIT_DETAIL_FIELD_KEYS,
} from '../match-grid'
import MatchPairingsPanel from './MatchPairingsPanel'

/** Larghezza fissa colonna Include (stessa in ogni tabella unità). */
const INCLUDE_COLUMN_WIDTH = 200

const COMPACT_ROW_SX = {
  '& .MuiTableCell-root': {
    py: 0.75,
    px: 1.25,
    fontSize: '0.8125rem',
    lineHeight: 1.35,
    borderColor: 'divider',
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-head': {
    py: 0.625,
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap',
  },
}

function getColumnCellSx(column, extra = {}) {
  const fixedWidth = column.width ?? (column.field === 'includeInMerge' ? INCLUDE_COLUMN_WIDTH : undefined)
  return {
    minWidth: column.minWidth ?? 96,
    ...(fixedWidth != null
      ? {
          width: fixedWidth,
          maxWidth: column.maxWidth ?? fixedWidth,
          boxSizing: 'border-box',
        }
      : {}),
    ...extra,
  }
}

/**
 * @param {object} column
 * @param {Record<string, unknown>} row
 */
function formatCellValue(column, row) {
  const value = row[column.field]
  if (value === null || value === undefined || value === '') return '—'
  if (column.valueFormatter) {
    return column.valueFormatter({ value })
  }
  return String(value)
}

/**
 * @param {object} props
 * @param {Record<string, unknown>[]} props.rows
 * @param {object[]} props.columns
 * @param {(column: object, row: Record<string, unknown>) => React.ReactNode} [props.renderCell]
 */
function MatchDetailTable({ rows, columns, renderCell, stickyLastField }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const stickyBg = isDark ? 'rgba(30,30,40,1)' : 'rgba(255,255,255,1)'

  if (!rows.length) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5 }}>
        <Trans>No records to display</Trans>
      </Typography>
    )
  }

  return (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        ...COMPACT_ROW_SX,
      }}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(99, 102, 241, 0.08)',
            }}
          >
            {columns.map((column) => {
              const isSticky = stickyLastField && column.field === stickyLastField
              return (
              <TableCell
                key={column.field}
                sx={getColumnCellSx(column, isSticky ? {
                  position: 'sticky',
                  right: 0,
                  bgcolor: isDark ? 'rgba(99,102,241,0.12)' : 'rgba(238,242,255,1)',
                  boxShadow: '-3px 0 6px rgba(0,0,0,0.06)',
                  zIndex: 2,
                } : {})}
              >
                {column.headerName ?? column.field}
              </TableCell>
            )})}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={String(row.id)}
              hover
              sx={{
                bgcolor: row?.isMatchCandidate
                  ? (isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)')
                  : index % 2 === 0
                    ? 'transparent'
                    : (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
                '&:last-child td': { borderBottom: 0 },
                '& td': {
                  borderBottom: '1px solid',
                  borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                },
              }}
            >
              {columns.map((column) => {
                const isSticky = stickyLastField && column.field === stickyLastField
                const custom = renderCell?.(column, row)
                const content = custom != null ? custom : (
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        fontSize: 'inherit',
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: column.field === 'Surname' || column.field === 'Name' ? 220 : 180,
                        ...(column.field === 'Person id'
                          ? { fontFamily: 'var(--font-mono, monospace)', fontWeight: 600 }
                          : {}),
                      }}
                      title={formatCellValue(column, row)}
                    >
                      {formatCellValue(column, row)}
                    </Typography>
                  )

                return (
                  <TableCell
                    key={column.field}
                    sx={getColumnCellSx(column, isSticky ? {
                      position: 'sticky',
                      right: 0,
                      bgcolor: row?.isMatchCandidate
                        ? (isDark ? 'rgba(34,197,94,0.14)' : 'rgba(220,252,231,1)')
                        : stickyBg,
                      boxShadow: '-3px 0 6px rgba(0,0,0,0.06)',
                      zIndex: 1,
                    } : {})}
                  >
                    {content}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

/**
 * @param {import('../match-grid').buildMatchGroup extends (...args: any[]) => infer R ? NonNullable<R> : never} group
 * @param {boolean} showUnits
 */
function formatGroupCounts(group, showUnits) {
  const parts = []
  if (showUnits && group.unitCount > 0) {
    parts.push(`${group.unitCount} unit${group.unitCount === 1 ? '' : 's'}`)
  }
  if (group.peopleCount > 0) {
    parts.push(`${group.peopleCount} person${group.peopleCount === 1 ? '' : 's'}`)
  }
  return parts.join(' · ')
}

function SectionTitle({ label, count }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 0.75,
        pl: 1,
        borderLeft: '3px solid',
        borderColor: 'primary.main',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        {label}
      </Typography>
      <Chip size="small" label={count} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
    </Box>
  )
}

function UnitPeopleHeader({ unit, peopleCount }) {
  const unitId = unit?.['Unit id'] ?? '—'
  const unitType = unit?.Type
  const typeLabel = unitType != null && unitType !== '' ? String(unitType) : null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 0.5,
        mt: 1,
        pl: 1.5,
        py: 0.5,
        borderLeft: '3px solid',
        borderColor: 'secondary.main',
        bgcolor: 'action.hover',
        borderRadius: '0 6px 6px 0',
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        <Trans>Unit</Trans>
      </Typography>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ fontFamily: 'var(--font-mono, monospace)' }}
      >
        {unitId}
      </Typography>
      {typeLabel ? (
        <>
          <Typography variant="caption" color="text.secondary">·</Typography>
          <Chip size="small" label={typeLabel} sx={{ height: 20, fontSize: '0.7rem' }} />
        </>
      ) : null}
      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', pr: 1 }}>
        <Trans>{peopleCount} in this unit</Trans>
      </Typography>
    </Box>
  )
}

/**
 * @param {object} props
 * @param {import('../match-grid').buildMatchGroup extends (...args: any[]) => infer R ? NonNullable<R> : never} props.group
 */
function PeopleSection({
  group,
  personColumns,
  renderPersonCell,
  showIncludeColumn,
  showUnits,
  crashId,
}) {
  const peopleRows = group.people.map((person) => ({
    ...person,
    [CRASH_ID_KEY]: crashId,
  }))

  const useUnitGrouping = showUnits && group.units.length > 0
  const { groups: unitGroups, orphans } = useUnitGrouping
    ? groupPeopleByUnit(group.units, peopleRows)
    : { groups: [], orphans: peopleRows }

  const tableProps = {
    columns: personColumns,
    renderCell: showIncludeColumn ? renderPersonCell : undefined,
    stickyLastField: showIncludeColumn ? 'includeInMerge' : undefined,
  }

  if (!useUnitGrouping) {
    return (
      <Box>
        <SectionTitle label={<Trans>People</Trans>} count={group.people.length} />
        <MatchDetailTable rows={peopleRows} {...tableProps} />
      </Box>
    )
  }

  return (
    <Box>
      <SectionTitle label={<Trans>People</Trans>} count={group.people.length} />
      <Stack gap={0.5}>
        {unitGroups.map(({ unit, people }) => (
          <Box key={String(unit.id)}>
            <UnitPeopleHeader unit={unit} peopleCount={people.length} />
            <MatchDetailTable rows={people} {...tableProps} />
          </Box>
        ))}
        {orphans.length > 0 ? (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5, mb: 0.5, display: 'block' }}>
              <Trans>Not linked to a unit</Trans>
            </Typography>
            <MatchDetailTable rows={orphans} {...tableProps} />
          </Box>
        ) : null}
      </Stack>
    </Box>
  )
}

/**
 * Vista gerarchica crash: riga summary collassabile + dettaglio units/people.
 */
export function CrashMatchGroupList({
  groups = [],
  configuration,
  organizationBasic,
  selectedCrashId = null,
  onSelectGroup,
  selectable = false,
  showIncludeColumn = false,
  onTogglePersonInclusion,
  defaultExpandAll = false,
  pairingsForSelectedGroup = null,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const { showUnits, showPeople } = getMatchDetailSections(organizationBasic)

  const [expandedIds, setExpandedIds] = useState(() => {
    if (defaultExpandAll) {
      return new Set(groups.map((g) => g.id))
    }
    if (groups.length === 1) {
      return new Set([groups[0].id])
    }
    return new Set()
  })

  const addExpandedGroupIds = useCallback((groupIds) => {
    if (!groupIds.length) return
    setExpandedIds((prev) => {
      const next = new Set(prev)
      let changed = false
      for (const id of groupIds) {
        if (!next.has(id)) {
          next.add(id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [])

  useEffect(() => {
    if (!defaultExpandAll || groups.length === 0) return
    addExpandedGroupIds(groups.map((g) => g.id))
  }, [defaultExpandAll, groups, addExpandedGroupIds])

  useEffect(() => {
    if (!selectedCrashId) return
    const target = groups.find((g) => g.crashId === selectedCrashId)
    if (!target) return
    addExpandedGroupIds([target.id])
  }, [selectedCrashId, groups, addExpandedGroupIds])

  const summaryColumns = useMemo(
    () => getMatchColumnsForFields(configuration ?? {}, CRASH_SUMMARY_FIELD_KEYS),
    [configuration],
  )

  const unitColumns = useMemo(
    () => getMatchColumnsForFields(configuration ?? {}, UNIT_DETAIL_FIELD_KEYS).map((col) => ({
      ...col,
      minWidth: col.field === 'Type' ? 120 : 100,
    })),
    [configuration],
  )

  const personFieldKeys = useMemo(
    () => getPersonDetailFieldKeys(organizationBasic),
    [organizationBasic],
  )

  const personColumns = useMemo(() => {
    const base = getMatchColumnsForFields(configuration ?? {}, personFieldKeys).map((col) => ({
      ...col,
      minWidth: col.field === 'Name' || col.field === 'Surname'
        ? 140
        : col.field === 'Person id'
          ? 88
          : 96,
    }))
    if (!showIncludeColumn) return base

    return [
      ...base,
      {
        field: 'includeInMerge',
        headerName: _(msg`Include`),
        minWidth: INCLUDE_COLUMN_WIDTH,
        width: INCLUDE_COLUMN_WIDTH,
        maxWidth: INCLUDE_COLUMN_WIDTH,
      },
    ]
  }, [configuration, personFieldKeys, showIncludeColumn, _])

  const renderPersonCell = useCallback((column, row) => {
    if (column.field !== 'includeInMerge') return undefined

    if (row?.isMatchCandidate) {
      return (
        <Box sx={{ width: INCLUDE_COLUMN_WIDTH, display: 'flex', justifyContent: 'flex-start' }}>
          <Chip
            size="small"
            color="success"
            variant="filled"
            label={_(msg`Match`)}
            sx={{ height: 24, fontWeight: 700, fontSize: '0.7rem' }}
          />
        </Box>
      )
    }

    const isGroupSelected = selectedCrashId != null &&
      selectedCrashId === row?.[CRASH_ID_KEY]

    if (!isGroupSelected) {
      return (
        <Box sx={{ width: INCLUDE_COLUMN_WIDTH }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>
            —
          </Typography>
        </Box>
      )
    }

    const checked = row?.includeInMerge === true
    return (
      <Box
        sx={{
          width: INCLUDE_COLUMN_WIDTH,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
        }}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Switch
          size="small"
          checked={checked}
          color="primary"
          sx={{ m: 0 }}
          onChange={(event) => {
            onTogglePersonInclusion?.(row?.personKey, event.target.checked)
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: checked ? 700 : 600,
            color: checked ? 'primary.main' : 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          {checked ? <Trans>Included</Trans> : <Trans>Include</Trans>}
        </Typography>
      </Box>
    )
  }, [selectedCrashId, onTogglePersonInclusion, _])

  const toggleExpanded = useCallback((groupId, event) => {
    event?.stopPropagation()
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }, [])

  const handleHeaderClick = useCallback(
    (group) => {
      if (selectable && group.crashId) {
        onSelectGroup?.(group.crashId)
        addExpandedGroupIds([group.id])
      }
    },
    [selectable, onSelectGroup, addExpandedGroupIds],
  )

  const formatSummaryValue = useCallback(
    (field, value) => {
      const col = summaryColumns.find((c) => c.field === field)
      if (col?.valueFormatter) {
        return col.valueFormatter({ value })
      }
      return value ?? '—'
    },
    [summaryColumns],
  )

  if (!groups.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        <Trans>No records to display</Trans>
      </Typography>
    )
  }

  const isDark = theme.palette.mode === 'dark'
  const borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
  const headerBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(248,250,252,1)'
  const selectedHeaderBg = isDark
    ? 'linear-gradient(90deg, rgba(99,102,241,0.22) 0%, rgba(99,102,241,0.08) 100%)'
    : 'linear-gradient(90deg, rgba(99,102,241,0.14) 0%, rgba(238,242,255,1) 100%)'
  const selectedBg = isDark ? 'rgba(99, 102, 241, 0.12)' : 'rgba(238, 242, 255, 0.85)'
  const detailBg = isDark ? 'rgba(0,0,0,0.15)' : 'rgba(248,250,252,0.65)'
  const selectedDetailBg = isDark ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.9)'

  return (
    <Stack gap={1.25}>
      {groups.map((group) => {
        const isExpanded = expandedIds.has(group.id)
        const isSelected = selectable && group.crashId === selectedCrashId
        const countsLabel = formatGroupCounts(group, showUnits)

        return (
          <Box
            key={group.id}
            aria-selected={isSelected}
            sx={{
              position: 'relative',
              border: '2px solid',
              borderColor: isSelected ? 'primary.main' : borderColor,
              borderRadius: 1.5,
              overflow: 'hidden',
              bgcolor: isSelected ? selectedBg : 'background.paper',
              boxShadow: isSelected
                ? '0 4px 14px rgba(99, 102, 241, 0.28), 0 0 0 1px rgba(99, 102, 241, 0.15)'
                : 'none',
              transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
              '&::before': isSelected
                ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: 'primary.main',
                    zIndex: 1,
                  }
                : undefined,
            }}
          >
            <Box
              role={selectable ? 'button' : undefined}
              tabIndex={selectable ? 0 : undefined}
              aria-pressed={selectable ? isSelected : undefined}
              onClick={() => handleHeaderClick(group)}
              onKeyDown={(event) => {
                if (!selectable) return
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleHeaderClick(group)
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0.5,
                py: 0.75,
                pl: isSelected ? 1.25 : 0.5,
                cursor: selectable ? 'pointer' : 'default',
                bgcolor: isSelected ? selectedHeaderBg : headerBg,
                borderBottom: isExpanded ? '1px solid' : 'none',
                borderColor: isSelected ? 'primary.light' : 'divider',
                '&:hover': selectable
                  ? {
                      bgcolor: isSelected
                        ? selectedHeaderBg
                        : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(241,245,249,1)'),
                    }
                  : {},
              }}
            >
              {selectable ? (
                <Tooltip title={isSelected ? _(msg`Selected match`) : _(msg`Select this crash`)}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: isSelected ? 'primary.main' : 'text.disabled',
                      ml: 0.25,
                    }}
                  >
                    {isSelected ? (
                      <CheckCircleIcon fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="small" />
                    )}
                  </Box>
                </Tooltip>
              ) : null}

              <IconButton
                size="small"
                aria-label={isExpanded ? _(msg`Collapse`) : _(msg`Expand`)}
                onClick={(event) => toggleExpanded(group.id, event)}
                sx={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: isSelected
                      ? 'repeat(3, minmax(0, 1fr)) auto auto'
                      : 'repeat(3, minmax(0, 1fr)) auto',
                  },
                  gap: 1.5,
                  alignItems: 'center',
                  pr: 1,
                }}
              >
                {CRASH_SUMMARY_FIELD_KEYS.map((field) => {
                  const value = group.summary[field]
                  if (value === undefined || value === null || value === '') return null
                  const label = summaryColumns.find((c) => c.field === field)?.headerName ?? field
                  const formatted = formatSummaryValue(field, value)
                  return (
                    <Box key={field} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
                        {label}
                      </Typography>
                      <Tooltip title={String(formatted)} placement="top-start">
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {formatted}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )
                })}
                {countsLabel ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={countsLabel}
                    sx={{
                      justifySelf: { sm: 'end' },
                      height: 24,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      borderColor: 'divider',
                    }}
                  />
                ) : null}
                {isSelected ? (
                  <Chip
                    size="small"
                    color="primary"
                    label={_(msg`Selected`)}
                    sx={{
                      justifySelf: { sm: 'end' },
                      height: 24,
                      fontWeight: 700,
                      fontSize: '0.7rem',
                    }}
                  />
                ) : null}
              </Box>
            </Box>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  px: 1.25,
                  py: 1.25,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                  bgcolor: isSelected ? selectedDetailBg : detailBg,
                  borderTop: '1px solid',
                  borderColor: isSelected ? 'primary.light' : 'divider',
                }}
              >
                {showUnits && group.units.length > 0 ? (
                  <Box>
                    <SectionTitle label={<Trans>Units</Trans>} count={group.units.length} />
                    <MatchDetailTable rows={group.units} columns={unitColumns} />
                  </Box>
                ) : null}

                {showPeople && group.people.length > 0 ? (
                  <PeopleSection
                    group={group}
                    personColumns={personColumns}
                    renderPersonCell={renderPersonCell}
                    showIncludeColumn={showIncludeColumn}
                    showUnits={showUnits}
                    crashId={group.crashId}
                  />
                ) : null}

                {isSelected && pairingsForSelectedGroup ? (
                  <MatchPairingsPanel {...pairingsForSelectedGroup} />
                ) : null}
              </Box>
            </Collapse>
          </Box>
        )
      })}
    </Stack>
  )
}

export default CrashMatchGroupList
